import type { PopoverProps } from 'react-aria-components';
import {
  Popover as AriaPopover,
  composeRenderProps,
  PopoverContext,

  useSlottedContext,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';

const styles = tv({
  base: 'bg-primary-contrast forced-colors:bg-[Canvas] shadow-2xl rounded-lg bg-clip-padding border border-secondary/10 outline-0',
  variants: {
    isEntering: {
      true: 'animate-in fade-in placement-bottom:slide-in-from-top-1 placement-top:slide-in-from-bottom-1 placement-left:slide-in-from-right-1 placement-right:slide-in-from-left-1 ease-out duration-200',
    },
    isExiting: {
      true: 'animate-out fade-out placement-bottom:slide-out-to-top-1 placement-top:slide-out-to-bottom-1 placement-left:slide-out-to-right-1 placement-right:slide-out-to-left-1 ease-in duration-150',
    },
  },
});

// Without an explicit maxHeight, react-aria constrains a popover to the space
// between its trigger and the viewport edge, so a menu opened near the bottom
// of the screen shrinks into a scrollable sliver instead of flipping above the
// trigger. Give menu popovers a bounded default height so the flip logic has a
// size to reason about; explicit maxHeight from the call site still wins.
const MENU_MAX_HEIGHT = 320;

export function Popover({
  children,
  className,
  ...props
}: PopoverProps) {
  const popoverContext = useSlottedContext(PopoverContext);
  const trigger = popoverContext?.trigger;
  const isSubmenu = trigger === 'SubmenuTrigger';
  const offset = isSubmenu ? 2 : 4;
  const isMenu = trigger === 'MenuTrigger' || isSubmenu;

  return (
    <AriaPopover
      offset={offset}
      {...(isMenu ? { maxHeight: MENU_MAX_HEIGHT } : {})}
      {...props}
      className={composeRenderProps(className, (className, renderProps) => styles({ ...renderProps, className }))}
    >
      {children}
    </AriaPopover>
  );
}
